/**
 * @file RestaurantQuickFilter.tsx
 * @description Компонент для отображения быстрых фильтров на странице ресторана
 */

import s from './RestaurantQuickFilter.module.sass'

import { FC, ReactNode, useMemo } from 'react'
import { createSelector } from '@reduxjs/toolkit'

import type { RootState } from '@app/model/store/client'
import { useAppSelector } from '@app/model/store/client'

import { RestaurantQuickFilterSkeleton } from '@widgets/Restaurant/RestaurantQuickFilter/index'
import { FilterItem } from '@widgets/Restaurant/RestaurantQuickFilter/ui/FilterItem'
import CalendarArrow from '@widgets/Restaurant/RestConcerts/ui/CalendarArrow/CalendarArrow'

import { Carousel } from '@shared/ui/Layout/Carousel'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { TypeQuickFilter } from '@shared/api/middleware/mappingAPI/city/types'

const SLIDE_MOVE_COUNT = 3

const selectQuickFilterState = createSelector(
  [
    (state: RootState) => state.pages?.app_home?.sections?.quickFilters,
    (state: RootState) => state.global?.currentCity?.id,
    (state: RootState) => Boolean(state.global?.cityRead || state.global?.currentCity),
  ],
  (quickFilters, currentCityId, hasCitySource) => ({ quickFilters, currentCityId, hasCitySource }),
)

type BuildQuickFiltersListParams = {
  list: TypeQuickFilter[] | null | undefined
  currentCityId: number | undefined
  citySlug: string
}

const buildQuickFiltersList = (params: BuildQuickFiltersListParams): ReactNode[] | undefined => {
  const { list, currentCityId, citySlug } = params
  return list?.map((filter) => <FilterItem key={`${currentCityId}-${filter.slug}`} data={filter} citySlug={citySlug} />)
}

/**
 * @function QuickFilterCarousel - карусель с контролами и слайдером
 */
const QuickFilterCarousel: FC<{ children: ReactNode[] }> = ({ children }) => (
  <Carousel>
    <div className={s.inner}>
      <div className={s.sliderControls}>
        <Carousel.Arrow direction='prev' hideDisabled={true}>
          <CalendarArrow state='prev' hasOverlay={false} hasButton={true} />
        </Carousel.Arrow>

        <Carousel.Arrow direction='next' hideDisabled={true}>
          <CalendarArrow state='next' hasOverlay={false} hasButton={true} />
        </Carousel.Arrow>
      </div>
      <Carousel.Slider shadow={true} slideMoveCount={SLIDE_MOVE_COUNT} className={s.slider}>
        {children}
      </Carousel.Slider>
    </div>
  </Carousel>
)

/**
 * @function QuickFilterContent - обёртка с каруселью
 */
const QuickFilterContent: FC<{ className: string; children: ReactNode[] }> = ({ className, children }) => (
  <div className={[s.wrapper, className].filter(Boolean).join(' ')}>
    <QuickFilterCarousel>{children}</QuickFilterCarousel>
  </div>
)

interface Props {
  className?: string
  list?: TypeQuickFilter[] | null
  citySlug?: string
  loading?: boolean
}

const RestaurantQuickFilter: FC<Props> = ({ className = '', list: listProp, citySlug = '', loading }) => {
  const { quickFilters, currentCityId, hasCitySource } = useAppSelector(selectQuickFilterState)

  const list = listProp ?? quickFilters?.list ?? []
  const hasCity = Boolean(citySlug) || hasCitySource
  const isLoading = loading ?? quickFilters?.loading ?? !listProp

  const filterItems = useMemo(
    () => buildQuickFiltersList({ list, currentCityId, citySlug }) ?? [],
    [list, currentCityId, citySlug],
  )

  const hasList = list.length > 0
  const showSkeleton = isLoading || !hasCity

  const skeletonView = showSkeleton && <RestaurantQuickFilterSkeleton className={className} />

  const mainView = !showSkeleton && (
    <BlockBoundary>
      {hasList && <QuickFilterContent className={className}>{filterItems}</QuickFilterContent>}
    </BlockBoundary>
  )

  const view = [skeletonView, mainView].find(Boolean)

  return <>{view}</>
}

export default RestaurantQuickFilter
